// packages/core/src/learning/schools/real-estate-housing.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "Real estate & housing". Matches boglehead-indexing.ts
// gold standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — held [] for the post-authoring cross-ref
//                     pass. Worldview/exhibit names: paula-pant. (Brandon
//                     Turner and Gary Keller appear in canon as authors only —
//                     NOT figures-tally entries, and neither currently exists
//                     as a FigureId.)
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const REAL_ESTATE_HOUSING_EXHIBIT: SchoolExhibitEntry = {
	id: 'real-estate-housing',
	tagline:
		'Real estate and housing believes where you live is both a life choice and a money choice.',
	worldviewSummary:
		'Real estate and housing is paying attention to the place where lifestyle, leverage, stability, location, and risk collide. This lens protects you from treating rent or a mortgage as just a monthly bill. It accepts that housing can build wealth, create stability, or give control, but it can also trap cash, add maintenance, concentrate risk, and reduce flexibility. The blind spot is assuming ownership is always superior or that renting is always wasted money. This lens is strongest when it helps users compare housing choices against real numbers, life stage, location, runway, and freedom.',
	coreBeliefs: [
		'Real estate and housing believes housing is one of the biggest money decisions most adults make.',
		'Real estate and housing believes ownership can create control, but control comes with costs.',
		'Real estate and housing believes renting can buy flexibility, especially when life is changing.',
		'Real estate and housing believes leverage can help or hurt depending on timing, cash flow, and risk.',
		'Real estate and housing believes location is not just lifestyle. It affects income, expenses, commute, relationships, and options.',
		'Real estate and housing believes maintenance, taxes, insurance, repairs, and transaction costs must be part of the real price.',
	],
	warnsAgainst: [
		'Calling rent wasted money without counting flexibility, risk, and opportunity cost.',
		'Calling homeownership wealth building without counting repairs, taxes, insurance, interest, and concentration risk.',
		'Buying a property because it feels adult instead of because the full tradeoff fits.',
		'Treating real estate income as passive when ownership still requires work, risk, and reserves.',
	],
	helps: 'This lens helps people deciding whether to rent, buy, move, house hack, stay flexible, or think about property as part of their wealth path. It may not fit when someone wants a simple answer, because housing decisions are deeply personal and depend on cash, location, time, risk, and life plans.',
	figures: [],
	canon: [
		{
			title: 'Afford Anything',
			author: 'Paula Pant',
			oneLineIdea:
				'Every housing choice has an opportunity cost, because money spent in one place cannot do every other job.',
		},
		{
			title: 'The Book on Rental Property Investing',
			author: 'Brandon Turner',
			oneLineIdea:
				'Rental property can produce income, but only after vacancy, repairs, financing, and management are counted.',
		},
		{
			title: 'The Millionaire Real Estate Investor',
			author: 'Gary Keller',
			oneLineIdea:
				'Real estate wealth depends on process, patience, financing, and discipline, not just owning property.',
		},
		{
			title: 'Rent versus buy framework',
			oneLineIdea:
				'The better choice depends on total cost, flexibility, time horizon, and what the money could do elsewhere.',
		},
		{
			title: 'House poor concept',
			oneLineIdea:
				'A home can look like progress while quietly removing cash, options, and peace.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-real-estate-housing',
	debateIds: [],
	applyActions: [
		{
			label: 'See whether your housing cost fits your current picture',
			action: {
				type: 'openDashboard',
				label: 'Check your housing load',
			},
		},
		{
			label: 'Test how a bigger housing payment changes your freedom path',
			action: {
				type: 'openSimulator',
				label: 'Simulate the housing cost',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Check how much runway you would have after moving or buying',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Write the real reason you want to rent, buy, or move',
			action: {
				type: 'noToolAction',
				label: 'Name the housing reason',
				prompt: 'Write the real reason you want this housing choice, then list the money cost, freedom cost, and lifestyle benefit.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Buying a home is not automatically wealth. Renting is not automatically failure.',
			kind: 'hook',
		},
		{
			line: 'Your housing choice can buy stability, or it can quietly buy stress.',
			kind: 'hook',
		},
		{
			line: 'What hidden cost did you miss before you rented, bought, or moved?',
			kind: 'interview-question',
		},
		{
			line: 'When did housing give you more freedom, and when did it trap you?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps people test the housing tradeoff before the payment becomes their life.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Real estate is not just property. It is location, debt, maintenance, flexibility, and time.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Ask someone if their home made them richer, calmer, or just more locked in.',
			kind: 'creator-prompt',
		},
		{
			line: 'Show the real monthly cost of ownership after repairs, taxes, insurance, and lost flexibility.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
